import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../context/AuthContext';
import { paymentAPI } from '../../lib/api';
import { UK_CITIES, USA_CITIES, CANADA_CITIES, AUSTRALIA_CITIES, CITIES_BY_COUNTRY, SUPPORTED_COUNTRIES } from '../../lib/utils';
import { toast } from 'sonner';
import { trackMultiApplyStep, trackMultiApplyPackageSelected, trackCVUpload, trackInitiateCheckout } from '../../lib/metaPixel';
import {
    Briefcase,
    ChevronLeft,
    ChevronRight,
    Check,
    Sparkles,
    Heart,
    Target,
    TrendingUp,
    Building2,
    Clock,
    MapPin,
    Car,
    Zap,
    Award,
    Crown,
    Calendar,
    Globe,
    Home,
    X,
    Search,
    Loader2,
    Upload,
    AlertCircle,
    FileText,
    Mail,
    ArrowRight,
    CheckCircle
} from 'lucide-react';

const TOTAL_STEPS = 13;

const INDUSTRIES = [
    'Information Technology & Telecommunications',
    'Healthcare & Life Sciences',
    'Financial Services & Insurance',
    'Sales & Business Development',
    'Marketing, Advertising & Public Relations',
    'Retail & Wholesale Trade',
    'Education & Training',
    'Human Resources & Recruiting',
    'Hospitality, Tourism & Leisure',
    'Manufacturing, Industrial & Engineering',
    'Logistics, Transportation & Warehousing',
    'Media, Design & Entertainment',
    'Architecture & Construction',
    'Real Estate & Facilities Management',
    'Government & Public Administration',
    'Legal Services',
    'Energy & Utilities',
    'Home & Personal Services',
    'Nonprofit & Charities',
    'Environmental Services',
    'Aerospace & Aviation',
    'Science & R&D (Non-Medical)',
    'Agriculture, Forestry & Fishing',
    'Defense & Military',
    'Public Safety & Emergency Services',
    'Private Security Services',
    'Mining & Natural Resources',
    'Sports & Athletics',
];

const WORK_TYPES = [
    { id: 'full-time', label: 'Full-time' },
    { id: 'part-time', label: 'Part-time' },
    { id: 'contract', label: 'Contract/Freelance' },
];

const SALARY_RANGES = [
    { id: '0-20000', label: '£0 - £20,000' },
    { id: '20000-30000', label: '£20,000 - £30,000' },
    { id: '30000-40000', label: '£30,000 - £40,000' },
    { id: '40000-50000', label: '£40,000 - £50,000' },
    { id: '50000-60000', label: '£50,000 - £60,000' },
    { id: '60000-75000', label: '£60,000 - £75,000' },
    { id: '75000-100000', label: '£75,000 - £100,000' },
    { id: '100000-150000', label: '£100,000 - £150,000' },
    { id: '150000+', label: '£150,000+' },
];

const MultiApplyPage = () => {
    const { isAuthenticated, login, user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [currentStep, setCurrentStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [citySearch, setCitySearch] = useState('');
    
    // Form data
    const [formData, setFormData] = useState({
        employmentStatus: '',
        jobTitle: '',
        experienceLevel: '',
        salaryRange: '',
        industries: [],
        workTypes: [],
        startDate: '',
        startASAP: false,
        willingToRelocate: null,
        country: 'United Kingdom',
        selectedCities: [],
        workLocationRemote: false,
        workLocationHybrid: false,
        hasDriverLicense: null,
        selectedPlan: '',
    });

    // Restore state from localStorage if returning from auth
    useEffect(() => {
        const returnFromAuth = location.state?.returnFromAuth;
        const savedStep = localStorage.getItem('multiApplyStep');
        const savedFormData = localStorage.getItem('multiApplyFormData');
        
        if (returnFromAuth && savedStep && savedFormData) {
            try {
                const parsedFormData = JSON.parse(savedFormData);
                setFormData(parsedFormData);
                // Move to step 13 (CV Upload) since user just authenticated
                setCurrentStep(13);
                // Clear localStorage after restoring
                localStorage.removeItem('multiApplyStep');
                localStorage.removeItem('multiApplyFormData');
            } catch (e) {
                console.error('Failed to restore Multi-Apply state:', e);
            }
        } else if (isAuthenticated && savedStep === '12') {
            // If user is authenticated and was at step 12, skip to step 13
            const savedFormDataCheck = localStorage.getItem('multiApplyFormData');
            if (savedFormDataCheck) {
                try {
                    const parsedFormData = JSON.parse(savedFormDataCheck);
                    setFormData(parsedFormData);
                    setCurrentStep(13);
                    localStorage.removeItem('multiApplyStep');
                    localStorage.removeItem('multiApplyFormData');
                } catch (e) {
                    console.error('Failed to restore Multi-Apply state:', e);
                }
            }
        }
    }, [location.state, isAuthenticated]);

    const updateFormData = (key, value) => {
        setFormData(prev => ({ ...prev, [key]: value }));
    };

    const toggleIndustry = (industry) => {
        if (industry === 'Any') {
            if (formData.industries.length === INDUSTRIES.length) {
                updateFormData('industries', []);
            } else {
                updateFormData('industries', [...INDUSTRIES]);
            }
        } else {
            const newIndustries = formData.industries.includes(industry)
                ? formData.industries.filter(i => i !== industry)
                : [...formData.industries, industry];
            updateFormData('industries', newIndustries);
        }
    };

    const toggleWorkType = (type) => {
        if (type === 'Any') {
            if (formData.workTypes.length === WORK_TYPES.length) {
                updateFormData('workTypes', []);
            } else {
                updateFormData('workTypes', WORK_TYPES.map(w => w.id));
            }
        } else {
            const newTypes = formData.workTypes.includes(type)
                ? formData.workTypes.filter(t => t !== type)
                : [...formData.workTypes, type];
            updateFormData('workTypes', newTypes);
        }
    };

    const addCity = (city) => {
        if (!formData.selectedCities.includes(city)) {
            updateFormData('selectedCities', [...formData.selectedCities, city]);
        }
        setCitySearch('');
    };

    const removeCity = (city) => {
        updateFormData('selectedCities', formData.selectedCities.filter(c => c !== city));
    };

    const nextStep = () => {
        if (currentStep <= TOTAL_STEPS) {
            // Track step progression in Meta Pixel
            const stepNames = ['Employment Status', 'Welcome', 'Job Title', 'Experience', 'Salary', 'Industries', 'Work Types', 'Start Date', 'Location', 'Work Preference', 'CV Upload', 'Package Selection'];
            trackMultiApplyStep(currentStep + 1, stepNames[currentStep] || `Step ${currentStep + 1}`);
            setCurrentStep(currentStep + 1);
        }
    };

    const prevStep = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handlePurchase = async (planId) => {
        if (!isAuthenticated) {
            toast.error('Please sign in to continue');
            login();
            return;
        }

        setLoading(true);
        updateFormData('selectedPlan', planId);
        
        // Track package selection in Meta Pixel
        const packagePrices = { starter: 36, pro: 74, pro_plus: 148 };
        const packageApps = { starter: 100, pro: 250, pro_plus: 250 };
        const packageNames = { starter: 'Starter Plan', pro: 'Pro Plan', pro_plus: 'Pro+ Plan' };
        trackMultiApplyPackageSelected(planId, packageApps[planId] || 100, packagePrices[planId] || 0);
        trackInitiateCheckout(`Multi-Apply ${planId}`, packagePrices[planId] || 0, 'multi_apply');

        try {
            const jobPreferences = {
                employment_status: formData.employmentStatus,
                job_titles: [formData.jobTitle],
                experience_level: formData.experienceLevel,
                industries: formData.industries,
                salary_range: formData.salaryRange,
                work_types: formData.workTypes,
                start_date: formData.startASAP ? new Date().toISOString().split('T')[0] : formData.startDate,
                start_asap: formData.startASAP,
                willing_to_relocate: formData.willingToRelocate,
                country: formData.country,
                cities: formData.selectedCities,
                work_location_remote: formData.workLocationRemote,
                work_location_hybrid: formData.workLocationHybrid,
                has_driver_license: formData.hasDriverLicense,
                cv_url: formData.cvUrl || null,
                cv_uploaded: formData.cvUploaded || false,
            };

            // Handle Super Admin free access
            if (user?.role === 'super_admin') {
                const originUrl = window.location.origin;
                const response = await paymentAPI.createMultiApplyCheckout(
                    planId, 
                    originUrl, 
                    jobPreferences
                );
                if (response.data.free_access) {
                    toast.success('Order created successfully! Super Admin free access granted.');
                    navigate('/dashboard');
                    return;
                }
            }
            
            // Regular users - navigate to in-app checkout page
            const priceInPence = Math.round(packagePrices[planId] * 100);
            navigate('/checkout', {
                state: {
                    type: 'multi_apply',
                    packageId: planId,
                    name: packageNames[planId],
                    amount: priceInPence,
                    applications: packageApps[planId],
                    tier: planId,
                    jobPreferences,
                    metadata: {
                        package_id: planId,
                        payment_type: 'multi_apply',
                    }
                }
            });
        } catch (error) {
            console.error('Checkout error:', error);
            toast.error('Failed to create checkout session');
        } finally {
            setLoading(false);
        }
    };

    // Progress bar
    const progress = ((currentStep) / TOTAL_STEPS) * 100;

    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return <Step1 formData={formData} updateFormData={updateFormData} onNext={nextStep} />;
            case 2:
                return <Step2 formData={formData} onNext={nextStep} />;
            case 3:
                return <Step3 formData={formData} updateFormData={updateFormData} onNext={nextStep} />;
            case 4:
                return <Step4 formData={formData} updateFormData={updateFormData} onNext={nextStep} />;
            case 5:
                return <Step5 formData={formData} updateFormData={updateFormData} onNext={nextStep} />;
            case 6:
                return <Step6 formData={formData} toggleIndustry={toggleIndustry} onNext={nextStep} />;
            case 7:
                return <Step7 formData={formData} toggleWorkType={toggleWorkType} onNext={nextStep} />;
            case 8:
                return <Step8 formData={formData} updateFormData={updateFormData} onNext={nextStep} />;
            case 9:
                return <Step9 formData={formData} updateFormData={updateFormData} onNext={nextStep} />;
            case 10:
                return <Step10 
                    formData={formData} 
                    updateFormData={updateFormData} 
                    citySearch={citySearch}
                    setCitySearch={setCitySearch}
                    addCity={addCity}
                    removeCity={removeCity}
                    onNext={nextStep} 
                />;
            case 11:
                return <Step11 formData={formData} updateFormData={updateFormData} onNext={nextStep} />;
            case 12:
                return <Step12EmailSignup formData={formData} updateFormData={updateFormData} onNext={nextStep} />;
            case 13:
                return <Step13CVUpload formData={formData} updateFormData={updateFormData} onNext={nextStep} />;
            default:
                return null;
        }
    };

    // If all steps completed, show pricing
    if (currentStep > TOTAL_STEPS) {
        return (
            <Layout>
                <PricingSection 
                    formData={formData} 
                    onSelectPlan={handlePurchase} 
                    loading={loading}
                    onBack={() => setCurrentStep(TOTAL_STEPS)}
                />
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="min-h-[calc(100vh-64px)] bg-gradient-to-br from-slate-50 via-white to-primary-50">
                {/* Progress Bar */}
                <div className="fixed top-16 left-0 right-0 z-40 bg-white/80 backdrop-blur-sm border-b">
                    <div className="max-w-4xl mx-auto px-4 py-3">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-neutral-600">
                                Step {currentStep} of {TOTAL_STEPS}
                            </span>
                            <span className="text-sm text-neutral-500">
                                {Math.round(progress)}% Complete
                            </span>
                        </div>
                        <div className="h-2 bg-neutral-200 rounded-full overflow-hidden">
                            <div 
                                className="h-full bg-gradient-to-r from-primary-500 to-primary-600 rounded-full transition-all duration-500 ease-out"
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div className="pt-24 pb-32">
                    <div className="max-w-2xl mx-auto px-4">
                        {renderStep()}
                    </div>
                </div>

                {/* Navigation */}
                <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg">
                    <div className="max-w-4xl mx-auto px-4 py-4 flex justify-between items-center">
                        <Button
                            variant="ghost"
                            onClick={prevStep}
                            disabled={currentStep === 1}
                            className="gap-2"
                            data-testid="prev-step-btn"
                        >
                            <ChevronLeft className="w-4 h-4" />
                            Back
                        </Button>
                        <div className="flex gap-1">
                            {Array.from({ length: TOTAL_STEPS }, (_, i) => (
                                <div
                                    key={i}
                                    className={`w-2 h-2 rounded-full transition-all ${
                                        i + 1 === currentStep 
                                            ? 'bg-primary-600 w-6' 
                                            : i + 1 < currentStep 
                                                ? 'bg-primary-400' 
                                                : 'bg-neutral-300'
                                    }`}
                                />
                            ))}
                        </div>
                        <div className="w-20" /> {/* Spacer for alignment */}
                    </div>
                </div>
            </div>
        </Layout>
    );
};

// Step 1: Employment Status
const Step1 = ({ formData, updateFormData, onNext }) => {
    const options = [
        { id: 'unemployed-urgent', label: 'Unemployed and really need a job', icon: '😰', color: 'border-red-200 hover:border-red-400 hover:bg-red-50' },
        { id: 'unemployed-relaxed', label: "Unemployed but not stressed about it", icon: '😌', color: 'border-blue-200 hover:border-blue-400 hover:bg-blue-50' },
        { id: 'badly-employed', label: 'Badly employed and in need of a job switch', icon: '😤', color: 'border-orange-200 hover:border-orange-400 hover:bg-orange-50' },
        { id: 'employed-open', label: 'Employed but open to greener pastures', icon: '🌱', color: 'border-green-200 hover:border-green-400 hover:bg-green-50' },
    ];

    const handleSelect = (id) => {
        updateFormData('employmentStatus', id);
        setTimeout(onNext, 300);
    };

    return (
        <div className="animate-fade-in">
            <div className="text-center mb-10">
                <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Briefcase className="w-8 h-8 text-primary-600" />
                </div>
                <h1 className="text-3xl font-bold text-neutral-900 mb-3">
                    Describe your current employment status
                </h1>
                <p className="text-neutral-500">
                    This helps us understand your situation better
                </p>
            </div>

            <div className="space-y-3">
                {options.map((option) => (
                    <button
                        key={option.id}
                        onClick={() => handleSelect(option.id)}
                        className={`w-full p-5 rounded-xl border-2 text-left transition-all flex items-center gap-4 ${option.color} ${
                            formData.employmentStatus === option.id 
                                ? 'ring-2 ring-primary-500 border-primary-500 bg-primary-50' 
                                : 'bg-white'
                        }`}
                        data-testid={`status-${option.id}`}
                    >
                        <span className="text-3xl">{option.icon}</span>
                        <span className="font-medium text-neutral-800">{option.label}</span>
                        {formData.employmentStatus === option.id && (
                            <Check className="w-5 h-5 text-primary-600 ml-auto" />
                        )}
                    </button>
                ))}
            </div>
        </div>
    );
};

// Step 2: Encouragement Message
const Step2 = ({ formData, onNext }) => {
    const messages = {
        'unemployed-urgent': {
            title: "Being unemployed is rough",
            subtitle: "Especially when you've got bills to pay. But never fear! Here at RecruitNearMe, we're ready to help you land your next job!",
            icon: Heart,
            color: 'text-red-500',
            bgColor: 'bg-red-50',
        },
        'unemployed-relaxed': {
            title: "Taking your time? Smart move!",
            subtitle: "Finding the right fit matters more than finding any job. Let us help you discover opportunities that match your goals.",
            icon: Sparkles,
            color: 'text-blue-500',
            bgColor: 'bg-blue-50',
        },
        'badly-employed': {
            title: "Ready for something better?",
            subtitle: "You deserve a job that values you. We'll help you find employers who appreciate your skills and experience.",
            icon: TrendingUp,
            color: 'text-orange-500',
            bgColor: 'bg-orange-50',
        },
        'employed-open': {
            title: "Smart to keep your options open!",
            subtitle: "The best time to look for a job is when you already have one. Let's find you that perfect next step.",
            icon: Target,
            color: 'text-green-500',
            bgColor: 'bg-green-50',
        },
    };

    const msg = messages[formData.employmentStatus] || messages['employed-open'];
    const Icon = msg.icon;

    return (
        <div className="animate-fade-in text-center">
            <div className={`w-24 h-24 ${msg.bgColor} rounded-3xl flex items-center justify-center mx-auto mb-8`}>
                <Icon className={`w-12 h-12 ${msg.color}`} />
            </div>
            
            <h1 className="text-3xl font-bold text-neutral-900 mb-4">
                {msg.title}
            </h1>
            <p className="text-lg text-neutral-600 mb-10 max-w-md mx-auto">
                {msg.subtitle}
            </p>

            <Button
                size="lg"
                onClick={onNext}
                className="h-14 px-10 bg-primary-600 hover:bg-primary-700 text-lg"
                data-testid="continue-btn"
            >
                Let's Get Started
                <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
        </div>
    );
};

// Step 3: Job Title
const Step3 = ({ formData, updateFormData, onNext }) => {
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && formData.jobTitle.trim()) {
            onNext();
        }
    };

    return (
        <div className="animate-fade-in">
            <div className="text-center mb-10">
                <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Target className="w-8 h-8 text-primary-600" />
                </div>
                <h1 className="text-3xl font-bold text-neutral-900 mb-3">
                    What's your desired job title?
                </h1>
            </div>

            <div className="space-y-6">
                <Input
                    type="text"
                    placeholder="e.g., Senior Python Developer, UX/UI Designer"
                    value={formData.jobTitle}
                    onChange={(e) => updateFormData('jobTitle', e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="h-14 text-lg px-5"
                    data-testid="job-title-input"
                />
                
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                    <p className="text-sm text-amber-800">
                        <strong>N.B:</strong> Please enter clear and specific job titles (e.g., Senior Python Developer, UX/UI Designer). 
                        Avoid generic or unclear titles like "Student" or "Developer". 
                        Specific titles help us match you with the best opportunities.
                    </p>
                </div>

                <Button
                    size="lg"
                    onClick={onNext}
                    disabled={!formData.jobTitle.trim()}
                    className="w-full h-14 bg-primary-600 hover:bg-primary-700 text-lg"
                    data-testid="next-btn"
                >
                    Continue
                    <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
            </div>
        </div>
    );
};

// Step 4: Experience Level
const Step4 = ({ formData, updateFormData, onNext }) => {
    const levels = [
        { id: 'entry', label: 'Entry-level', desc: 'First full-time role in the field', icon: '🌱' },
        { id: 'junior', label: 'Junior/Associate', desc: 'Works independently on well-scoped tasks', icon: '📚' },
        { id: 'mid', label: 'Mid-level', desc: 'Owns projects, may mentor others', icon: '💼' },
        { id: 'senior', label: 'Senior/Lead', desc: 'Leads complex work and sets direction', icon: '🎯' },
        { id: 'director', label: 'Director', desc: 'Oversees teams/functions and drives strategy', icon: '🏢' },
        { id: 'executive', label: 'Executive', desc: 'VP/C-level leadership and org-wide decisions', icon: '👔' },
    ];

    const handleSelect = (id) => {
        updateFormData('experienceLevel', id);
        setTimeout(onNext, 300);
    };

    return (
        <div className="animate-fade-in">
            <div className="text-center mb-10">
                <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <TrendingUp className="w-8 h-8 text-primary-600" />
                </div>
                <h1 className="text-3xl font-bold text-neutral-900 mb-3">
                    What's your target experience level?
                </h1>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {levels.map((level) => (
                    <button
                        key={level.id}
                        onClick={() => handleSelect(level.id)}
                        className={`p-4 rounded-xl border-2 text-left transition-all hover:border-primary-300 hover:bg-primary-50 ${
                            formData.experienceLevel === level.id 
                                ? 'ring-2 ring-primary-500 border-primary-500 bg-primary-50' 
                                : 'border-neutral-200 bg-white'
                        }`}
                        data-testid={`level-${level.id}`}
                    >
                        <div className="flex items-start gap-3">
                            <span className="text-2xl">{level.icon}</span>
                            <div>
                                <p className="font-semibold text-neutral-900">{level.label}</p>
                                <p className="text-sm text-neutral-500">{level.desc}</p>
                            </div>
                        </div>
                    </button>
                ))}
            </div>
        </div>
    );
};

// Step 5: Salary Range
const Step5 = ({ formData, updateFormData, onNext }) => {
    const handleSelect = (rangeId) => {
        updateFormData('salaryRange', rangeId);
        setTimeout(onNext, 300);
    };

    return (
        <div className="animate-fade-in">
            <div className="text-center mb-10">
                <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <span className="text-3xl">💰</span>
                </div>
                <h1 className="text-3xl font-bold text-neutral-900 mb-3">
                    What's your preferred salary range?
                </h1>
            </div>

            <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {SALARY_RANGES.map((range) => (
                    <button
                        key={range.id}
                        onClick={() => handleSelect(range.id)}
                        className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center justify-between ${
                            formData.salaryRange === range.id
                                ? 'ring-2 ring-primary-500 border-primary-500 bg-primary-50'
                                : 'border-neutral-200 bg-white hover:border-primary-300'
                        }`}
                        data-testid={`salary-${range.id}`}
                    >
                        <span className="font-medium text-neutral-800">{range.label}</span>
                        {formData.salaryRange === range.id && (
                            <Check className="w-5 h-5 text-primary-600" />
                        )}
                    </button>
                ))}
            </div>
                
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl mt-6">
                <p className="text-sm text-blue-800">
                    <strong>N.B:</strong> We'll use this to help match you with roles that meet your salary expectations. 
                    Please note: many employers don't include salary information in their job listings, 
                    so we might not always be able to filter based on pay.
                </p>
            </div>
        </div>
    );
};

// Step 6: Industries
const Step6 = ({ formData, toggleIndustry, onNext }) => {
    const allSelected = formData.industries.length === INDUSTRIES.length;

    return (
        <div className="animate-fade-in">
            <div className="text-center mb-8">
                <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Building2 className="w-8 h-8 text-primary-600" />
                </div>
                <h1 className="text-3xl font-bold text-neutral-900 mb-3">
                    What industries do you want to work in?
                </h1>
                <p className="text-neutral-500">Select all that apply</p>
            </div>

            <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                {/* Any (Select All) option */}
                <button
                    onClick={() => toggleIndustry('Any')}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center justify-between ${
                        allSelected
                            ? 'ring-2 ring-primary-500 border-primary-500 bg-primary-50'
                            : 'border-neutral-200 bg-white hover:border-primary-300'
                    }`}
                    data-testid="industry-any"
                >
                    <span className="font-semibold text-neutral-900">Any (Select All)</span>
                    <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center ${
                        allSelected ? 'bg-primary-600 border-primary-600' : 'border-neutral-300'
                    }`}>
                        {allSelected && <Check className="w-4 h-4 text-white" />}
                    </div>
                </button>

                {/* Industry options */}
                <div className="grid grid-cols-1 gap-2">
                    {INDUSTRIES.map((industry) => (
                        <button
                            key={industry}
                            onClick={() => toggleIndustry(industry)}
                            className={`w-full p-3 rounded-lg border text-left transition-all flex items-center justify-between ${
                                formData.industries.includes(industry)
                                    ? 'border-primary-500 bg-primary-50'
                                    : 'border-neutral-200 bg-white hover:border-primary-300'
                            }`}
                            data-testid={`industry-${industry.replace(/\s+/g, '-').toLowerCase()}`}
                        >
                            <span className="text-sm text-neutral-700">{industry}</span>
                            <div className={`w-5 h-5 rounded flex items-center justify-center ${
                                formData.industries.includes(industry) ? 'bg-primary-600' : 'border border-neutral-300'
                            }`}>
                                {formData.industries.includes(industry) && <Check className="w-3 h-3 text-white" />}
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            <div className="mt-6 sticky bottom-0 bg-gradient-to-t from-white pt-4">
                <Button
                    size="lg"
                    onClick={onNext}
                    disabled={formData.industries.length === 0}
                    className="w-full h-14 bg-primary-600 hover:bg-primary-700 text-lg"
                    data-testid="next-btn"
                >
                    Continue ({formData.industries.length} selected)
                    <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
            </div>
        </div>
    );
};

// Step 7: Work Type
const Step7 = ({ formData, toggleWorkType, onNext }) => {
    const allSelected = formData.workTypes.length === WORK_TYPES.length;

    return (
        <div className="animate-fade-in">
            <div className="text-center mb-10">
                <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Clock className="w-8 h-8 text-primary-600" />
                </div>
                <h1 className="text-3xl font-bold text-neutral-900 mb-3">
                    What type of work are you open to?
                </h1>
            </div>

            <div className="space-y-3">
                {/* Any (Select All) */}
                <button
                    onClick={() => toggleWorkType('Any')}
                    className={`w-full p-5 rounded-xl border-2 text-left transition-all flex items-center justify-between ${
                        allSelected
                            ? 'ring-2 ring-primary-500 border-primary-500 bg-primary-50'
                            : 'border-neutral-200 bg-white hover:border-primary-300'
                    }`}
                    data-testid="work-type-any"
                >
                    <span className="font-semibold text-neutral-900">Any (Select All)</span>
                    <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center ${
                        allSelected ? 'bg-primary-600 border-primary-600' : 'border-neutral-300'
                    }`}>
                        {allSelected && <Check className="w-4 h-4 text-white" />}
                    </div>
                </button>

                {WORK_TYPES.map((type) => (
                    <button
                        key={type.id}
                        onClick={() => toggleWorkType(type.id)}
                        className={`w-full p-5 rounded-xl border-2 text-left transition-all flex items-center justify-between ${
                            formData.workTypes.includes(type.id)
                                ? 'border-primary-500 bg-primary-50'
                                : 'border-neutral-200 bg-white hover:border-primary-300'
                        }`}
                        data-testid={`work-type-${type.id}`}
                    >
                        <span className="font-medium text-neutral-800">{type.label}</span>
                        <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center ${
                            formData.workTypes.includes(type.id) ? 'bg-primary-600 border-primary-600' : 'border-neutral-300'
                        }`}>
                            {formData.workTypes.includes(type.id) && <Check className="w-4 h-4 text-white" />}
                        </div>
                    </button>
                ))}
            </div>

            <Button
                size="lg"
                onClick={onNext}
                disabled={formData.workTypes.length === 0}
                className="w-full h-14 mt-8 bg-primary-600 hover:bg-primary-700 text-lg"
                data-testid="next-btn"
            >
                Continue
                <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
        </div>
    );
};

// Step 8: Start Date
const Step8 = ({ formData, updateFormData, onNext }) => {
    const handleASAP = () => {
        updateFormData('startASAP', true);
        updateFormData('startDate', new Date().toISOString().split('T')[0]);
        setTimeout(onNext, 300);
    };

    const handleDateChange = (e) => {
        updateFormData('startASAP', false);
        updateFormData('startDate', e.target.value);
    };

    return (
        <div className="animate-fade-in">
            <div className="text-center mb-10">
                <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Calendar className="w-8 h-8 text-primary-600" />
                </div>
                <h1 className="text-3xl font-bold text-neutral-900 mb-3">
                    When are you available to start?
                </h1>
                <p className="text-neutral-500">We'll use this to match you with roles that fit your timeline.</p>
            </div>

            <div className="space-y-4">
                {/* ASAP Option */}
                <button
                    onClick={handleASAP}
                    className={`w-full p-5 rounded-xl border-2 text-left transition-all flex items-center gap-4 ${
                        formData.startASAP
                            ? 'ring-2 ring-primary-500 border-primary-500 bg-primary-50'
                            : 'border-neutral-200 bg-white hover:border-primary-300'
                    }`}
                    data-testid="start-asap"
                >
                    <span className="text-3xl">⚡</span>
                    <div className="flex-1">
                        <p className="font-semibold text-neutral-900">ASAP - I'm ready to start immediately</p>
                        <p className="text-sm text-neutral-500">Available from today</p>
                    </div>
                    {formData.startASAP && (
                        <Check className="w-5 h-5 text-primary-600" />
                    )}
                </button>

                {/* Specific Date Option */}
                <div className="relative">
                    <p className="text-sm font-medium text-neutral-600 mb-2">Or select a specific date:</p>
                    <Input
                        type="date"
                        value={formData.startASAP ? '' : formData.startDate}
                        onChange={handleDateChange}
                        min={new Date().toISOString().split('T')[0]}
                        className="h-14 text-lg px-5"
                        data-testid="start-date-input"
                    />
                </div>

                <Button
                    size="lg"
                    onClick={onNext}
                    disabled={!formData.startASAP && !formData.startDate}
                    className="w-full h-14 bg-primary-600 hover:bg-primary-700 text-lg mt-4"
                    data-testid="next-btn"
                >
                    Continue
                    <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
            </div>
        </div>
    );
};

// Step 9: Relocation
const Step9 = ({ formData, updateFormData, onNext }) => {
    const handleSelect = (value) => {
        updateFormData('willingToRelocate', value);
        setTimeout(onNext, 300);
    };

    return (
        <div className="animate-fade-in">
            <div className="text-center mb-10">
                <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <MapPin className="w-8 h-8 text-primary-600" />
                </div>
                <h1 className="text-3xl font-bold text-neutral-900 mb-3">
                    Are you willing to relocate for a job?
                </h1>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <button
                    onClick={() => handleSelect(true)}
                    className={`p-8 rounded-xl border-2 text-center transition-all ${
                        formData.willingToRelocate === true
                            ? 'ring-2 ring-primary-500 border-primary-500 bg-primary-50'
                            : 'border-neutral-200 bg-white hover:border-primary-300'
                    }`}
                    data-testid="relocate-yes"
                >
                    <span className="text-4xl block mb-3">✈️</span>
                    <span className="font-semibold text-lg text-neutral-900">Yes</span>
                </button>
                <button
                    onClick={() => handleSelect(false)}
                    className={`p-8 rounded-xl border-2 text-center transition-all ${
                        formData.willingToRelocate === false
                            ? 'ring-2 ring-primary-500 border-primary-500 bg-primary-50'
                            : 'border-neutral-200 bg-white hover:border-primary-300'
                    }`}
                    data-testid="relocate-no"
                >
                    <span className="text-4xl block mb-3">🏠</span>
                    <span className="font-semibold text-lg text-neutral-900">No</span>
                </button>
            </div>
        </div>
    );
};

// Step 10: Location Preferences
const Step10 = ({ formData, updateFormData, citySearch, setCitySearch, addCity, removeCity, onNext }) => {
    // Get cities for selected country
    const countryCities = CITIES_BY_COUNTRY[formData.country] || UK_CITIES;
    const filteredCities = countryCities
        .filter(city => city.toLowerCase().includes(citySearch.toLowerCase()))
        .filter(city => !formData.selectedCities.includes(city))
        .slice(0, 10);

    // Handle country change - clear selected cities when country changes
    const handleCountryChange = (newCountry) => {
        updateFormData('country', newCountry);
        updateFormData('selectedCities', []); // Clear cities when country changes
        setCitySearch(''); // Clear search
    };

    return (
        <div className="animate-fade-in">
            <div className="text-center mb-8">
                <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Globe className="w-8 h-8 text-primary-600" />
                </div>
                <h1 className="text-3xl font-bold text-neutral-900 mb-3">
                    Where would you like to work?
                </h1>
            </div>

            <div className="space-y-6">
                {/* Country Selection */}
                <div>
                    <label className="text-sm font-medium text-neutral-700 mb-2 block">Country</label>
                    <div className="grid grid-cols-2 gap-2">
                        {SUPPORTED_COUNTRIES.map((country) => (
                            <button
                                key={country}
                                onClick={() => handleCountryChange(country)}
                                className={`p-3 rounded-xl border-2 text-left transition-all flex items-center gap-3 ${
                                    formData.country === country
                                        ? 'ring-2 ring-primary-500 border-primary-500 bg-primary-50'
                                        : 'border-neutral-200 bg-white hover:border-primary-300'
                                }`}
                                data-testid={`country-${country.replace(/\s+/g, '-').toLowerCase()}`}
                            >
                                <span className="text-xl">
                                    {country === 'United Kingdom' && '🇬🇧'}
                                    {country === 'United States' && '🇺🇸'}
                                    {country === 'Canada' && '🇨🇦'}
                                    {country === 'Australia' && '🇦🇺'}
                                </span>
                                <span className="font-medium text-neutral-800 text-sm">{country}</span>
                                {formData.country === country && (
                                    <Check className="w-4 h-4 text-primary-600 ml-auto" />
                                )}
                            </button>
                        ))}
                    </div>
                </div>

                {/* City Search */}
                <div>
                    <label className="text-sm font-medium text-neutral-700 mb-2 block">Select Cities (Optional)</label>
                    <div className="relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                        <Input
                            type="text"
                            placeholder="Search cities..."
                            value={citySearch}
                            onChange={(e) => setCitySearch(e.target.value)}
                            className="h-12 pl-12"
                            data-testid="city-search"
                        />
                    </div>
                    
                    {/* Search Results */}
                    {citySearch && (
                        <div className="mt-2 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                            {filteredCities.length > 0 ? (
                                filteredCities.map(city => (
                                    <button
                                        key={city}
                                        onClick={() => addCity(city)}
                                        className="w-full px-4 py-2 text-left hover:bg-primary-50 text-sm"
                                    >
                                        {city}
                                    </button>
                                ))
                            ) : (
                                <p className="px-4 py-2 text-sm text-neutral-500">No cities found</p>
                            )}
                        </div>
                    )}

                    {/* Selected Cities */}
                    {formData.selectedCities.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                            {formData.selectedCities.map(city => (
                                <Badge 
                                    key={city} 
                                    variant="secondary" 
                                    className="px-3 py-1 cursor-pointer hover:bg-red-100"
                                    onClick={() => removeCity(city)}
                                >
                                    {city}
                                    <X className="w-3 h-3 ml-1" />
                                </Badge>
                            ))}
                        </div>
                    )}
                </div>

                {/* Work Location Type - Allow multiple selections */}
                <div>
                    <label className="text-sm font-medium text-neutral-700 mb-3 block">Work Location Preference (Select all that apply)</label>
                    <div className="grid grid-cols-2 gap-3">
                        <button
                            onClick={() => updateFormData('workLocationRemote', !formData.workLocationRemote)}
                            className={`p-4 rounded-xl border-2 text-center transition-all ${
                                formData.workLocationRemote
                                    ? 'ring-2 ring-primary-500 border-primary-500 bg-primary-50'
                                    : 'border-neutral-200 bg-white hover:border-primary-300'
                            }`}
                            data-testid="location-remote"
                        >
                            <Home className="w-6 h-6 mx-auto mb-2 text-primary-600" />
                            <span className="font-medium text-neutral-900">Full Remote</span>
                            {formData.workLocationRemote && (
                                <Check className="w-5 h-5 mx-auto mt-2 text-primary-600" />
                            )}
                        </button>
                        <button
                            onClick={() => updateFormData('workLocationHybrid', !formData.workLocationHybrid)}
                            className={`p-4 rounded-xl border-2 text-center transition-all ${
                                formData.workLocationHybrid
                                    ? 'ring-2 ring-primary-500 border-primary-500 bg-primary-50'
                                    : 'border-neutral-200 bg-white hover:border-primary-300'
                            }`}
                            data-testid="location-in-person"
                        >
                            <Building2 className="w-6 h-6 mx-auto mb-2 text-primary-600" />
                            <span className="font-medium text-neutral-900">In-person/Hybrid</span>
                            {formData.workLocationHybrid && (
                                <Check className="w-5 h-5 mx-auto mt-2 text-primary-600" />
                            )}
                        </button>
                    </div>
                </div>

                <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                    <p className="text-sm text-blue-800">
                        <strong>N.B:</strong> Select your country first, then choose cities. You can select multiple cities and both Remote AND In-person/Hybrid options. 
                        The more options you select, the more job opportunities you're likely to find.
                    </p>
                </div>

                <Button
                    size="lg"
                    onClick={onNext}
                    className="w-full h-14 bg-primary-600 hover:bg-primary-700 text-lg"
                    data-testid="next-btn"
                >
                    Continue
                    <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
            </div>
        </div>
    );
};

// Step 11: Driver's License
const Step11 = ({ formData, updateFormData, onNext }) => {
    const options = [
        { id: 'yes', label: 'Yes', icon: '🚗' },
        { id: 'no', label: 'No', icon: '🚶' },
        { id: 'prefer-not', label: 'I do not wish to provide this information', icon: '🔒' },
    ];

    const handleSelect = (id) => {
        updateFormData('hasDriverLicense', id);
        setTimeout(onNext, 300);
    };

    return (
        <div className="animate-fade-in">
            <div className="text-center mb-10">
                <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Car className="w-8 h-8 text-primary-600" />
                </div>
                <h1 className="text-3xl font-bold text-neutral-900 mb-3">
                    Do you have a current driver's license?
                </h1>
            </div>

            <div className="space-y-3">
                {options.map((option) => (
                    <button
                        key={option.id}
                        onClick={() => handleSelect(option.id)}
                        className={`w-full p-5 rounded-xl border-2 text-left transition-all flex items-center gap-4 ${
                            formData.hasDriverLicense === option.id
                                ? 'ring-2 ring-primary-500 border-primary-500 bg-primary-50'
                                : 'border-neutral-200 bg-white hover:border-primary-300'
                        }`}
                        data-testid={`license-${option.id}`}
                    >
                        <span className="text-2xl">{option.icon}</span>
                        <span className="font-medium text-neutral-800">{option.label}</span>
                        {formData.hasDriverLicense === option.id && (
                            <Check className="w-5 h-5 text-primary-600 ml-auto" />
                        )}
                    </button>
                ))}
            </div>
        </div>
    );
};

// Step 12: Email Signup (before CV Upload)
const Step12EmailSignup = ({ formData, updateFormData, onNext }) => {
    const { isAuthenticated, user, loginWithGoogle } = useAuth();
    const [email, setEmail] = useState(formData.signupEmail || '');
    const [password, setPassword] = useState('');
    const [name, setName] = useState(formData.signupName || '');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showEmailForm, setShowEmailForm] = useState(false);

    // If already authenticated, skip this step
    useEffect(() => {
        if (isAuthenticated && user) {
            onNext();
        }
    }, [isAuthenticated, user, onNext]);

    // Handle Google Sign In
    const handleGoogleSignIn = () => {
        // Save current form data to localStorage before redirecting
        localStorage.setItem('multiApplyFormData', JSON.stringify(formData));
        localStorage.setItem('multiApplyStep', '12');
        loginWithGoogle();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            // Try login first, if fails try register
            let response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password })
            });

            let data = await response.json();

            // If login failed with "not found" error, try to register
            if (!response.ok && data.detail?.toLowerCase().includes('invalid')) {
                response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/register`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email, password, name: name || email.split('@')[0] })
                });
                data = await response.json();
            }

            if (!response.ok) {
                throw new Error(data.detail || 'Authentication failed');
            }

            // Store session token
            if (data.token || data.session_token) {
                localStorage.setItem('session_token', data.token || data.session_token);
            }

            // Save email to form data
            updateFormData('signupEmail', email);
            updateFormData('signupName', name);

            // Save form data to localStorage before reload
            localStorage.setItem('multiApplyFormData', JSON.stringify(formData));
            localStorage.setItem('multiApplyStep', '12');

            // Reload to update auth state - the useEffect will restore state and advance to step 13
            window.location.reload();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // If already authenticated, show loading
    if (isAuthenticated) {
        return (
            <div className="animate-fade-in text-center py-12">
                <Loader2 className="w-10 h-10 text-primary-500 mx-auto animate-spin" />
                <p className="text-neutral-600 mt-4">Continuing...</p>
            </div>
        );
    }

    return (
        <div className="animate-fade-in">
            <div className="text-center mb-10">
                <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Mail className="w-8 h-8 text-primary-600" />
                </div>
                <h1 className="text-3xl font-bold text-neutral-900 mb-3">
                    Quick Sign In
                </h1>
                <p className="text-neutral-600 max-w-md mx-auto">
                    Sign in to save your preferences and upload your CV
                </p>
            </div>

            <div className="max-w-md mx-auto space-y-6">
                {/* Google Sign In - Primary Option */}
                <button
                    onClick={handleGoogleSignIn}
                    className="w-full py-4 bg-white border-2 border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50 text-neutral-800 font-semibold rounded-xl transition-all flex items-center justify-center gap-3 shadow-sm"
                >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Continue with Google
                </button>

                {/* Divider */}
                <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-neutral-200"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                        <span className="px-4 bg-white text-neutral-500">or continue with email</span>
                    </div>
                </div>

                {/* Email Form Toggle */}
                {!showEmailForm ? (
                    <button
                        onClick={() => setShowEmailForm(true)}
                        className="w-full py-3 text-primary-600 hover:text-primary-700 font-medium transition-colors flex items-center justify-center gap-2"
                    >
                        <Mail className="w-4 h-4" />
                        Use email instead
                    </button>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-neutral-700 mb-2">
                                Email Address
                            </label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                required
                                className="w-full px-4 py-3 border border-neutral-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-neutral-700 mb-2">
                                Password
                            </label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Enter your password"
                                required
                                minLength={6}
                                className="w-full px-4 py-3 border border-neutral-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all"
                            />
                            <p className="text-xs text-neutral-500 mt-1">New user? We'll create an account for you</p>
                        </div>

                        {error && (
                            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-2">
                                <AlertCircle className="w-4 h-4 shrink-0" />
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading || !email || !password}
                            className="w-full py-4 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    Signing in...
                                </>
                            ) : (
                                <>
                                    Continue
                                    <ArrowRight className="w-5 h-5" />
                                </>
                            )}
                        </button>
                    </form>
                )}

                <div className="p-4 bg-primary-50 border border-primary-100 rounded-xl">
                    <p className="text-sm text-primary-800 text-center">
                        <CheckCircle className="w-4 h-4 inline-block mr-1 -mt-0.5" />
                        Your information is secure and will only be used for job applications
                    </p>
                </div>
            </div>
        </div>
    );
};

// Step 13: CV Upload
const Step13CVUpload = ({ formData, updateFormData, onNext }) => {
    const { user } = useAuth();
    const [uploading, setUploading] = useState(false);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [error, setError] = useState(null);

    // Check if user already has a CV
    useEffect(() => {
        if (user?.cv_url) {
            setUploadedFile({
                name: user.cv_filename || 'Existing CV',
                url: user.cv_url,
                isExisting: true
            });
        }
    }, [user]);

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
        if (!allowedTypes.includes(file.type)) {
            setError('Please upload a PDF, DOC, or DOCX file');
            return;
        }

        // Validate file size (10MB max)
        if (file.size > 10 * 1024 * 1024) {
            setError('File size must be less than 10MB');
            return;
        }

        setError(null);
        setUploading(true);

        try {
            const formDataUpload = new FormData();
            formDataUpload.append('file', file);

            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/upload-cv`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('session_token')}`
                },
                body: formDataUpload
            });

            const result = await response.json();

            if (response.ok) {
                setUploadedFile({
                    name: file.name,
                    url: result.cv_url,
                    isExisting: false
                });
                updateFormData('cvUploaded', true);
                updateFormData('cvUrl', result.cv_url);
            } else {
                setError(result.detail || 'Failed to upload CV');
            }
        } catch (err) {
            setError('Failed to upload CV. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    const handleContinue = () => {
        if (uploadedFile) {
            onNext();
        }
    };

    const handleSkip = () => {
        updateFormData('cvUploaded', false);
        updateFormData('cvUrl', null);
        onNext();
    };

    return (
        <div className="animate-fade-in">
            <div className="text-center mb-10">
                <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <FileText className="w-8 h-8 text-primary-600" />
                </div>
                <h1 className="text-3xl font-bold text-neutral-900 mb-3">
                    Upload Your CV
                </h1>
                <p className="text-neutral-600 max-w-md mx-auto">
                    We'll use your CV to apply for jobs on your behalf. Make sure it's up to date!
                </p>
            </div>

            <div className="max-w-md mx-auto space-y-6">
                {/* Upload Area */}
                {!uploadedFile ? (
                    <label 
                        className={`block p-8 border-2 border-dashed rounded-xl text-center cursor-pointer transition-all ${
                            uploading 
                                ? 'border-primary-300 bg-primary-50' 
                                : 'border-neutral-300 hover:border-primary-400 hover:bg-neutral-50'
                        }`}
                    >
                        <input
                            type="file"
                            accept=".pdf,.doc,.docx"
                            onChange={handleFileChange}
                            disabled={uploading}
                            className="hidden"
                        />
                        {uploading ? (
                            <div className="space-y-3">
                                <Loader2 className="w-10 h-10 text-primary-500 mx-auto animate-spin" />
                                <p className="text-primary-600 font-medium">Uploading...</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                <Upload className="w-10 h-10 text-neutral-400 mx-auto" />
                                <div>
                                    <p className="font-medium text-neutral-700">Click to upload your CV</p>
                                    <p className="text-sm text-neutral-500 mt-1">PDF, DOC, or DOCX (Max 10MB)</p>
                                </div>
                            </div>
                        )}
                    </label>
                ) : (
                    <div className="p-6 bg-emerald-50 border-2 border-emerald-200 rounded-xl">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                                <FileText className="w-6 h-6 text-emerald-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-medium text-emerald-900 truncate">{uploadedFile.name}</p>
                                <p className="text-sm text-emerald-600">
                                    {uploadedFile.isExisting ? 'Previously uploaded' : 'Successfully uploaded'}
                                </p>
                            </div>
                            <button
                                onClick={() => {
                                    setUploadedFile(null);
                                    updateFormData('cvUploaded', false);
                                    updateFormData('cvUrl', null);
                                }}
                                className="p-2 text-neutral-400 hover:text-red-500 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                )}

                {/* Error Message */}
                {error && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-700">
                        <AlertCircle className="w-5 h-5 shrink-0" />
                        <p className="text-sm">{error}</p>
                    </div>
                )}

                {/* Action Buttons */}
                <div className="space-y-3 pt-4">
                    <Button
                        onClick={handleContinue}
                        disabled={!uploadedFile}
                        className="w-full h-12 bg-primary-600 hover:bg-primary-700"
                        data-testid="cv-continue-btn"
                    >
                        Continue with this CV
                        <ChevronRight className="w-5 h-5 ml-2" />
                    </Button>
                    
                    <button
                        onClick={handleSkip}
                        className="w-full py-3 text-neutral-500 hover:text-neutral-700 text-sm transition-colors"
                        data-testid="cv-skip-btn"
                    >
                        Skip for now (you can upload later)
                    </button>
                </div>

                {/* Info Box */}
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex gap-3">
                        <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                        <div className="text-sm text-amber-800">
                            <p className="font-medium">Why upload your CV?</p>
                            <p className="mt-1">Our team will use your CV to apply for jobs matching your preferences. A well-formatted CV increases your chances of getting responses.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Pricing Section
const PricingSection = ({ formData, onSelectPlan, loading, onBack }) => {
    const { user } = useAuth();
    const isSuperAdmin = user?.role === 'super_admin';
    
    const plans = [
        {
            id: 'starter',
            name: 'STARTER PLAN',
            tagline: 'IDEAL FOR FOCUSED JOB SEARCHING',
            monthlyPrice: 36,
            period: 'month',
            features: [
                'Up to 100 job applications per month',
                'Applications tailored to each role',
                'Basic CV improvement suggestions',
                'Email support',
            ],
            icon: Zap,
            color: 'from-slate-600 to-slate-800',
        },
        {
            id: 'pro',
            name: 'PRO PLAN',
            tagline: 'FASTER INTERVIEWS, STRONGER APPLICATIONS',
            monthlyPrice: 74,
            period: 'month',
            popular: true,
            features: [
                'Everything in Starter',
                'Up to 250 job applications per month',
                'Professionally optimised CV',
                'Custom cover letter for each role',
                'Supporting statement writing',
                'Priority support',
            ],
            icon: Award,
            color: 'from-primary-600 to-primary-800',
        },
        {
            id: 'pro_plus',
            name: 'PRO+ PLAN',
            tagline: 'MAXIMUM EXPOSURE, MAXIMUM SUPPORT',
            monthlyPrice: 148,
            period: 'month',
            features: [
                'Everything in Pro',
                'Premium CV optimisation & full review',
                'Tailored cover letters for every role',
                'Supporting statement writing',
                'Follow-up emails to hiring managers',
                'Dedicated account manager',
                '1-on-1 career coaching session',
            ],
            icon: Crown,
            color: 'from-amber-500 to-amber-700',
        },
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-primary-950 to-slate-900 py-16">
            <div className="max-w-6xl mx-auto px-4">
                {/* Header */}
                <div className="text-center mb-12">
                    {isSuperAdmin ? (
                        <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 mb-6">
                            <Crown className="w-3 h-3 mr-1" />
                            Super Admin - FREE ACCESS
                        </Badge>
                    ) : (
                        <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 mb-6">
                            <Sparkles className="w-3 h-3 mr-1" />
                            From no response to dream job
                        </Badge>
                    )}
                    <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                        Choose Your Plan
                    </h1>
                    <p className="text-lg text-neutral-300 max-w-2xl mx-auto">
                        {isSuperAdmin 
                            ? "As a Super Admin, you have FREE access to all plans. Select any plan to create your order."
                            : "You're almost there! Select a plan to start your job search journey."
                        }
                    </p>
                </div>

                {/* Pricing Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    {plans.map((plan) => (
                        <div
                            key={plan.id}
                            className={`relative rounded-2xl overflow-hidden ${
                                plan.popular ? 'ring-2 ring-primary-400 shadow-2xl shadow-primary-500/20' : ''
                            }`}
                        >
                            {plan.popular && (
                                <div className="absolute top-0 left-0 right-0 bg-primary-500 text-white text-center py-2 text-sm font-semibold">
                                    MOST POPULAR
                                </div>
                            )}
                            
                            <div className={`bg-gradient-to-br ${plan.color} p-6 ${plan.popular ? 'pt-12' : ''}`}>
                                <div className="flex items-center gap-3 mb-2">
                                    <plan.icon className="w-6 h-6 text-white/80" />
                                    <h3 className="text-white font-bold">{plan.name}</h3>
                                </div>
                                <p className="text-white/70 text-sm">{plan.tagline}</p>
                            </div>

                            <div className="bg-white p-6">
                                <div className="mb-6">
                                    <div className="flex items-baseline gap-2">
                                        <span className="text-4xl font-bold text-neutral-900">£{plan.monthlyPrice}</span>
                                    </div>
                                    <p className="text-neutral-500">/month</p>
                                </div>

                                <ul className="space-y-3 mb-6">
                                    {plan.features.map((feature, idx) => (
                                        <li key={idx} className="flex items-start gap-2">
                                            <Check className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                                            <span className="text-sm text-neutral-600">{feature}</span>
                                        </li>
                                    ))}
                                </ul>

                                <Button
                                    onClick={() => onSelectPlan(plan.id)}
                                    disabled={loading}
                                    className={`w-full h-12 ${
                                        isSuperAdmin
                                            ? 'bg-amber-500 hover:bg-amber-600'
                                            : plan.popular 
                                                ? 'bg-primary-600 hover:bg-primary-700' 
                                                : 'bg-neutral-900 hover:bg-neutral-800'
                                    }`}
                                    data-testid={`select-plan-${plan.id}`}
                                >
                                    {loading ? (
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                    ) : isSuperAdmin ? (
                                        <>
                                            <Crown className="w-4 h-4 mr-2" />
                                            Get Free Access
                                        </>
                                    ) : (
                                        <>Get Started</>
                                    )}
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Back Button */}
                <div className="text-center">
                    <Button
                        variant="ghost"
                        onClick={onBack}
                        className="text-white/70 hover:text-white hover:bg-white/10"
                    >
                        <ChevronLeft className="w-4 h-4 mr-2" />
                        Go Back to Edit Preferences
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default MultiApplyPage;
